import { useState, useEffect } from "react";
import { useAuth } from "@/lib/context/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PositionSelect } from "@/components/auth/PositionSelect";
import { Combobox } from "@/components/ui/combobox";
import { createLogger } from "@/lib/utils/logger";
import { User } from "lucide-react";
import { ChangeEvent } from "react";

const logger = createLogger('frontend', 'ProfilePage');

interface SchoolOption {
  value: string;
  label: string;
  districtId?: string;
}

const Profile = () => {
  const { user, profile, updateAuthState } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [schoolDistrict, setSchoolDistrict] = useState("");
  const [customSchoolDistrict, setCustomSchoolDistrict] = useState("");
  const [school, setSchool] = useState("");
  const [customSchool, setCustomSchool] = useState("");
  const [position, setPosition] = useState("");
  const [customPosition, setCustomPosition] = useState("");

  const [schoolDistricts, setSchoolDistricts] = useState<{ value: string; label: string }[]>([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [positions, setPositions] = useState<{ value: string; label: string }[]>([]);
  const [showCustomSchoolDistrict, setShowCustomSchoolDistrict] = useState(false);
  const [showCustomSchool, setShowCustomSchool] = useState(false);
  const [filteredSchools, setFilteredSchools] = useState<{ value: string; label: string }[]>([]);

  const [imageUploading, setImageUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(profile?.image_url || profile?.photo_url || "");

  useEffect(() => {
    if (profile) {
      logger.info("Profile data loaded", JSON.stringify(profile));
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      setSchoolDistrict(profile.school_district || "");
      setSchool(profile.school || "");
      setPosition(profile.position || "");
      
      if (profile.school_district) {
        const isCustomDistrict = !schoolDistricts.some(d => d.value === profile.school_district);
        if (isCustomDistrict) {
          setShowCustomSchoolDistrict(true);
          setSchoolDistrict("Other");
          setCustomSchoolDistrict(profile.school_district || "");
        }
      }
      
      if (profile.school) {
        const isCustomSchool = !schools.some(s => s.value === profile.school);
        if (isCustomSchool) {
          setShowCustomSchool(true);
          setSchool("Other");
          setCustomSchool(profile.school || "");
        }
      }
      
      if (profile.position) {
        const isCustomPosition = !positions.some(p => p.value === profile.position);
        if (isCustomPosition) {
          setCustomPosition(profile.position || "");
        }
      }
    }
    if (user) {
      setEmail(user.email || "");
    }
  }, [user, profile, schools, schoolDistricts, positions]);

  useEffect(() => {
    async function fetchData() {
      try {
        const { data: districtsData, error: districtsError } = await supabase
          .from("school_districts")
          .select("id, name")
          .order("name");

        if (districtsError) throw districtsError;
        
        const formattedDistricts = districtsData.map((district) => ({
          value: district.name,
          label: district.name,
        }));
        
        setSchoolDistricts(formattedDistricts);

        const { data: schoolsData, error: schoolsError } = await supabase
          .from("schools")
          .select("id, name, district_id")
          .order("name");

        if (schoolsError) throw schoolsError;
        
        const formattedSchools = schoolsData.map((school) => ({
          value: school.name,
          label: school.name,
          districtId: school.district_id,
        }));
        
        setSchools(formattedSchools);

        const { data: positionsData, error: positionsError } = await supabase
          .from("positions")
          .select("id, title, category")
          .order("title");

        if (positionsError) throw positionsError;
        
        const uniqueTitles = new Set();
        const formattedPositions = positionsData
          .filter(position => {
            if (uniqueTitles.has(position.title)) {
              return false;
            }
            uniqueTitles.add(position.title);
            return true;
          })
          .map((position) => ({
            value: position.title,
            label: position.title,
          }));
        
        setPositions(formattedPositions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    let schoolsToShow: { value: string; label: string }[] = [];
    
    if (schoolDistrict && schoolDistrict !== "Other") {
      const districtSchools = schools
        .filter(school => {
          const district = schoolDistricts.find(d => d.value === schoolDistrict);
          return district && school.districtId === district.value;
        })
        .map(school => ({
          value: school.value,
          label: school.label
        }));
      
      schoolsToShow = districtSchools;
    } else {
      schoolsToShow = schools.map(school => ({
        value: school.value,
        label: school.label
      }));
    }
    
    if (school && !schoolsToShow.some(s => s.value === school)) {
      schoolsToShow.push({
        value: school,
        label: school
      });
    }
    
    setFilteredSchools(schoolsToShow);
  }, [schoolDistrict, schools, schoolDistricts, school]);

  const handleSchoolDistrictChange = (value: string) => {
    setSchoolDistrict(value);
    setShowCustomSchoolDistrict(value === "Other");
    
    if (!school) {
      setSchool("");
      setShowCustomSchool(false);
      setCustomSchool("");
    }
  };

  const handleSchoolChange = (value: string) => {
    setSchool(value);
    setShowCustomSchool(value === "Other");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user || !profile) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      logger.info("Updating profile with values", JSON.stringify({ 
        firstName, 
        lastName, 
        schoolDistrict, 
        school, 
        position 
      }));
      
      const finalSchoolDistrict = schoolDistrict === "Other" ? customSchoolDistrict : schoolDistrict;
      const finalSchool = school === "Other" ? customSchool : school;
      const finalPosition = position === "Other" ? customPosition : position;

      const { data, error } = await supabase
        .from("profiles")
        .update({
          first_name: firstName,
          last_name: lastName,
          school_district: finalSchoolDistrict,
          school: finalSchool,
          position: finalPosition,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select();

      if (error) {
        logger.error("Error updating profile", error.message);
        throw error;
      }

      logger.info("Profile update successful", JSON.stringify(data));
      
      // Refresh the auth state to get the updated profile
      await updateAuthState(user, false);
      
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully",
      });
    } catch (error) {
      logger.error("Error updating profile", error instanceof Error ? error.message : String(error));
      toast({
        title: "Update failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "U";
  };

  const schoolDistrictsWithOther = [
    ...schoolDistricts,
    ...(schoolDistricts.some(d => d.value === "Other") ? [] : [{ value: "Other", label: "Other (specify)" }])
  ];

  const schoolsWithOther = [
    ...filteredSchools,
    ...(filteredSchools.some(s => s.value === "Other") ? [] : [{ value: "Other", label: "Other (specify)" }])
  ];

  // Handle image upload
  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setImageUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}.${fileExt}`;
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage.from('profile-images').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      // Always get and use the public URL
      const { data } = supabase.storage.from('profile-images').getPublicUrl(filePath);
      if (!data?.publicUrl) throw new Error('Failed to get public URL');
      setImageUrl(data.publicUrl);
      // Save the public URL to the profile
      const { error: updateError } = await supabase.from('profiles').update({ image_url: data.publicUrl, updated_at: new Date().toISOString() }).eq('id', user.id);
      if (updateError) throw updateError;
      await updateAuthState(user, false);
      toast({ title: 'Profile image updated', description: 'Your profile picture has been updated.' });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message || 'Failed to upload image', variant: 'destructive' });
    } finally {
      setImageUploading(false);
    }
  };

  // Sync imageUrl with profile.image_url after upload and on reload
  useEffect(() => {
    if (profile) {
      setImageUrl(profile.image_url || profile.photo_url || "");
    }
  }, [profile]);

  // Log the imageUrl for debugging
  console.log('Profile imageUrl:', imageUrl);

  return (
    <div className="w-full py-1 px-1 animate-fade-in">
      <div className="page-header">
        <div className="flex items-center gap-2">
          <User className="h-6 w-6 text-[#004161]" />
          <h1>Profile Settings</h1>
        </div>
        <p>Manage your account preferences</p>
      </div>
      
      <Card className="border-accent-light/30 mt-4">
        <CardHeader className="flex flex-row items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-lg bg-primary text-primary-foreground font-title">{getInitials()}</span>
          </div>
          <div>
            <CardTitle className="font-title text-primary">Personal Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="font-body">First Name</Label>
                <Input 
                  id="firstName" 
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name" 
                  className="border-accent-light/20 focus-visible:ring-primary/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="font-body">Last Name</Label>
                <Input 
                  id="lastName" 
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last Name" 
                  className="border-accent-light/20 focus-visible:ring-primary/20"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body">Email</Label>
              <Input 
                id="email" 
                type="email"
                value={email}
                disabled
                placeholder="you@example.com" 
                className="bg-accent-subtle/50 border-accent-light/20"
              />
              <p className="text-xs text-muted-foreground mt-1 font-section italic">
                Email cannot be changed directly. Contact support for assistance.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="font-body">School District</Label>
              <Combobox
                options={schoolDistrictsWithOther}
                value={schoolDistrict}
                onChange={handleSchoolDistrictChange}
                placeholder="Select your school district"
                emptyMessage="No school districts found."
                searchPlaceholder="Search school districts..."
                className="border-accent-light/20"
              />
              {showCustomSchoolDistrict && (
                <div className="mt-2">
                  <Label htmlFor="customSchoolDistrict" className="font-body">Specify School District</Label>
                  <Input
                    id="customSchoolDistrict"
                    value={customSchoolDistrict}
                    onChange={(e) => setCustomSchoolDistrict(e.target.value)}
                    placeholder="Enter your school district"
                    className="mt-1 border-accent-light/20 focus-visible:ring-primary/20"
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="font-body">School</Label>
              <Combobox
                options={schoolsWithOther}
                value={school}
                onChange={handleSchoolChange}
                placeholder="Select your school"
                emptyMessage="No schools found."
                searchPlaceholder="Search schools..."
                disabled={!schoolDistrict && schoolDistricts.length > 0}
                className="border-accent-light/20"
              />
              {showCustomSchool && (
                <div className="mt-2">
                  <Label htmlFor="customSchool" className="font-body">Specify School</Label>
                  <Input
                    id="customSchool"
                    value={customSchool}
                    onChange={(e) => setCustomSchool(e.target.value)}
                    placeholder="Enter your school"
                    className="mt-1 border-accent-light/20 focus-visible:ring-primary/20"
                  />
                </div>
              )}
            </div>
            
            <PositionSelect
              position={position}
              positions={positions}
              onChange={setPosition}
              customPosition={customPosition}
              onCustomPositionChange={setCustomPosition}
            />
            
            <Button 
              type="submit" 
              className="w-full font-title"
              disabled={isLoading}
              variant="default"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
